import { useAuth } from "../../contexts/AuthContext";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { DataTable } from '../../components/DataTable/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { dailyReportsAPI, regieReportsAPI } from '../../services/api';
import { DailyReport } from '../../types/api';
import { useSnackbar } from 'notistack';

const columns = [
  {
    id: 'date',
    label: 'Datum',
    format: (value: string) => format(new Date(value), 'dd.MM.yyyy', { locale: de }),
  },
  { id: 'project_name', label: 'Projekt' },
  { id: 'weather', label: 'Wetter' },
  {
    id: 'created_at',
    label: 'Erstellt am',
    format: (value: string) =>
      format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: de }),
  },
];

export const DailyReportsList: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Erkennen ob wir Tagesberichte oder Regieberichte anzeigen
  const isRegieReports = window.location.pathname.includes('/regie-reports');
  
  const isAdmin = user?.role === "admin";
  const isManagement = user?.role === "management";
  const isBauleiter = user?.role === "bauleiter";
  const isForeman = user?.role === "foreman";
  const isInstaller = user?.role === "installer";
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure haben eingeschränkte Rechte
  const canSeeRestricted = isForeman || isInstaller;
  
  const canCreateReports = canSeeAll || canSeeRestricted;
  
  // Admin, Management, Bauleiter können ALLE Berichte bearbeiten
  const canEditAllReports = canSeeAll;
  
  // 🔒 Individuelle Berechtigung pro Bericht (wie bei Tagesberichten)
  const canEditReport = (report: DailyReport) => {
    if (canEditAllReports) return true; // Admin, Management & Bauleiter können alle bearbeiten
    
    if (canSeeRestricted) { // Foreman & Installer
      const isOwnReport = report.created_by === user?.id;
      const isDraft = report.status === 'entwurf';
      return isOwnReport && isDraft; // Muss eigener Bericht UND Entwurf sein
    }
    return false;
  };
  
  const canDeleteReports = canSeeAll; // Beide: Nur Admin, Management, Bauleiter können löschen
  const { enqueueSnackbar } = useSnackbar();
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const api = isRegieReports ? regieReportsAPI : dailyReportsAPI;
      const response = await api.getReports();
      setReports(response.items);
    } catch (error) {
      console.error('Error fetching reports:', error);
      const reportType = isRegieReports ? 'Regieberichte' : 'Tagesberichte';
      enqueueSnackbar(`Fehler beim Laden der ${reportType}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleCreate = () => {
    const basePath = isRegieReports ? '/regie-reports' : '/tagesberichte';
    navigate(`${basePath}/new`);
  };

  const handleEdit = (report: DailyReport) => {
    const basePath = isRegieReports ? '/regie-reports' : '/tagesberichte';
    navigate(`${basePath}/${report.id}/edit`);
  };

  const handleView = (report: DailyReport) => {
    const basePath = isRegieReports ? '/regie-reports' : '/tagesberichte';
    navigate(`${basePath}/${report.id}`);
  };

  const handleDelete = async () => {
    if (!selectedReport) return;

    try {
      const api = isRegieReports ? regieReportsAPI : dailyReportsAPI;
      await api.deleteReport(selectedReport.id);
      const reportType = isRegieReports ? 'Regiebericht' : 'Tagesbericht';
      enqueueSnackbar(`${reportType} erfolgreich gelöscht`, { variant: 'success' });
      fetchReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      const reportType = isRegieReports ? 'Regieberichts' : 'Tagesberichts';
      enqueueSnackbar(`Fehler beim Löschen des ${reportType}`, { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedReport(null);
    }
  };

  const confirmDelete = (report: DailyReport) => {
    setSelectedReport(report);
    setDeleteDialogOpen(true);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {isRegieReports ? 'Regieberichte' : 'Tagesberichte'}
        </Typography>
        
        {canCreateReports && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            {isRegieReports ? 'Neuer Regiebericht' : 'Neuer Tagesbericht'}
          </Button>
        )}

      </Box>

      <Card>
        <CardContent>
          <DataTable
            columns={columns}
            rows={reports}
            loading={loading}
            initialOrder="desc"
            persistKey={isRegieReports ? 'regie-reports' : 'daily-reports-legacy'}
            onView={handleView}
            onEdit={(report: DailyReport) => {
              if (canEditReport(report)) {
                handleEdit(report);
              }
            }}
            onDelete={(report: DailyReport) => {
              if (canDeleteReports) {
                confirmDelete(report);
              }
            }}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Regiebericht löschen"
        message="Sind Sie sicher, dass Sie diesen Regiebericht löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedReport(null);
        }}
        severity="error"
      />
    </Box>
  );
}; 